import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { SideSheetPrimitive } from './side-sheet.primitive';
import * as Styled from './side-sheet.styles';
import {
  dimmerDurationEnter,
  dimmerTransitionName,
  sideSheetDurationEnter,
  sideSheetTransitionName,
} from './side-sheet.styles';
import type { SideSheetProps } from './side-sheet.types';

export const SideSheet = React.forwardRef<HTMLDivElement, SideSheetProps>(
  (
    {
      children,
      className,
      dimmerTitle,
      focusTrapProps,
      keepSideSheetInDom,
      onOutsideClick,
      onSwipeLeft,
      shouldDisableBodyScroll = true,
      show = false,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const backupRef = React.useRef<HTMLDivElement>(null);
    const sideSheetRef = forwardedRef || backupRef;
    const dimmerRef = React.useRef<HTMLDivElement>(null);

    const disableScrolling = (): void => {
      document.body.style.overflow = 'hidden';
    };

    const enableScrolling = (): void => {
      document.body.style.overflow = 'visible';
    };

    const closeModal = (event: React.SyntheticEvent): void => {
      event.stopPropagation();
      enableScrolling();
      if (onOutsideClick) onOutsideClick(event);
    };

    // Body scroll management
    React.useEffect(() => {
      if (!shouldDisableBodyScroll) {
        return;
      }

      if (show) {
        disableScrolling();
      }

      return () => {
        enableScrolling();
      };
    }, [show, shouldDisableBodyScroll]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (show && onOutsideClick && event.key === 'Escape') {
          event.preventDefault();
          onOutsideClick(event);
        }
      };

      if (show) {
        document.addEventListener('keydown', handleKey);
      }

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [onOutsideClick, show]);

    // Focus trap configuration
    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
      escapeDeactivates: Boolean(onOutsideClick),
      fallbackFocus: () => {
        // If there is no focusable element, focus the container itself
        const container =
          sideSheetRef instanceof Function ? null : sideSheetRef.current;
        if (container) return container;
        return document.body;
      },
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    const sideSheetTransition = (
      <CSSTransition
        appear={show}
        classNames={sideSheetTransitionName}
        in={show}
        key="sideSheet"
        mountOnEnter={!keepSideSheetInDom}
        timeout={parseInt(sideSheetDurationEnter)}
        unmountOnExit={!keepSideSheetInDom}
        nodeRef={sideSheetRef}
      >
        <FocusTrap
          active={show}
          {...otherFocusTrapProps}
          focusTrapOptions={mergedFocusTrapOptions}
        >
          <SideSheetPrimitive
            className={className}
            data-show={show}
            onSwipeLeft={onSwipeLeft}
            ref={sideSheetRef}
            {...props}
          >
            {children}
          </SideSheetPrimitive>
        </FocusTrap>
      </CSSTransition>
    );

    const dimmerTransition = (
      <CSSTransition
        appear={show}
        classNames={dimmerTransitionName}
        in={show}
        key="dimmer"
        mountOnEnter
        onEnter={() => {
          if (shouldDisableBodyScroll) {
            disableScrolling();
          }
        }}
        onExited={() => {
          if (shouldDisableBodyScroll) {
            enableScrolling();
          }
        }}
        timeout={parseInt(dimmerDurationEnter)}
        unmountOnExit
        nodeRef={dimmerRef}
      >
        <Styled.Dimmer
          $isNotClickable={!onOutsideClick}
          data-qa="side-sheet-dimmer"
          data-testid="side-sheet-dimmer"
          onClick={closeModal}
          title={dimmerTitle}
          ref={dimmerRef}
        />
      </CSSTransition>
    );

    return (
      <>
        <Portal>{sideSheetTransition}</Portal>
        {dimmerTransition}
      </>
    );
  }
);

SideSheet.displayName = 'SideSheet';
